import { Colors, Lightning, Registry } from '@lightningjs/sdk';
import { debounce, getImageTextureObj } from 'support/generalUtils';
import { constants } from '../../../aliases';
import TextLabel from '../TextLabel';

export type ToastStyles = {
  bgColor: string;
  strokeColor: string;
  iconPath: string;
  iconSize?: number;
  maxWidth?: number;
  fontSize?: number;
  fontColor?: string;
  toastDuration?: number;
};

const MANUAL_ANNOUNCE_DELAY = constants.timers.manualAnnounceDelay;
const INVISIBLE = constants.ui.invisible;
const TOAST_DURATION = 5000;
const MOUNT_X = 0.5;
const DEFAULT_X = 960;
const DEFAULT_Y = 60;

const DEFAULT_MAX_WIDTH = 960;
const DEFAULT_H = 74;
const DEFAULT_FONT_SIZE = 28;
const DEFAULT_FONT_COLOR = 'labelText';
const DEFAULT_RADIUS = 4;
const DEFAULT_BORDER_WIDTH = 2;

const ICON_SIZE = 24;
const PADDING_WIDTH = 24;
const MESSAGE_MARGIN_TOP = 7;
const MESSAGE_MARGIN_LEFT = 12;

const FIXED_WIDTH = PADDING_WIDTH * 2 + ICON_SIZE + MESSAGE_MARGIN_LEFT;

export interface ToastTemplateSpec extends Lightning.Component.TemplateSpec {
  toastStyles: ToastStyles;
  message: string;

  StartIcon: Lightning.textures.ImageTexture;
  Message: typeof TextLabel;
}

export default class Toast
  extends Lightning.Component<ToastTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<ToastTemplateSpec>
{
  private _maxWidth = DEFAULT_MAX_WIDTH;
  private _toastDuration = TOAST_DURATION;
  private _timeoutId: number | undefined = undefined;

  protected _StartIcon = this.getByRef('StartIcon')!;
  protected _Message = this.getByRef('Message')!;

  get label() {
    return this._Message.text?.text.replace(/<\/?b>/g, '') ?? '';
  }

  set toastStyles(toastStyles: ToastStyles) {
    this.patch({
      color: Colors(toastStyles.bgColor).get(),
      shader: { strokeColor: Colors(toastStyles.strokeColor).get() },
    });
    const iconSize = toastStyles.iconSize || ICON_SIZE;
    this._StartIcon.patch(
      getImageTextureObj(toastStyles.iconPath, iconSize, iconSize),
    );

    if (toastStyles.maxWidth) this._maxWidth = toastStyles.maxWidth;
    if (toastStyles.toastDuration)
      this._toastDuration = toastStyles.toastDuration;

    this._Message.patch({
      text: {
        fontSize: toastStyles.fontSize || DEFAULT_FONT_SIZE,
        textColor: Colors(toastStyles.fontColor || DEFAULT_FONT_COLOR).get(),
      },
    });
  }

  set message(message: string) {
    // Make feintly visible so component can render the text to trigger txLoaded event
    this.patch({ alpha: INVISIBLE });
    this._Message.patch({ text: { text: message } });
  }

  static override _template(): Lightning.Component.Template<ToastTemplateSpec> {
    return {
      mountX: MOUNT_X,
      x: DEFAULT_X,
      y: DEFAULT_Y,
      w: DEFAULT_MAX_WIDTH,
      h: DEFAULT_H,
      alpha: INVISIBLE,
      rect: true,
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: DEFAULT_RADIUS,
        stroke: DEFAULT_BORDER_WIDTH,
      },
      flex: {
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
      zIndex: constants.ui.zIndex.spinnerLayer,
      StartIcon: {
        flexItem: {
          marginLeft: PADDING_WIDTH,
        },
        shader: null,
      },
      Message: {
        type: TextLabel,
        flexItem: {
          marginTop: MESSAGE_MARGIN_TOP,
          marginLeft: MESSAGE_MARGIN_LEFT,
        },
        shader: null,
        text: {
          textAlign: 'left',
          wordWrap: false,
          maxLines: 1,
          maxLinesSuffix: '...',
          wordWrapWidth: DEFAULT_MAX_WIDTH - FIXED_WIDTH,
          textOverflow: 'ellipsis',
          advancedRenderer: true,
        },
      },
    };
  }

  override _setup() {
    this._Message.on('txLoaded', () => {
      this.updateToastWidth();
    });
  }

  override _inactive() {
    if (this.alpha !== 0) {
      this.hide();
    }
    this.cleanTimers();
  }

  show() {
    this.patch({ alpha: 1 });
    this.cleanTimers();
    this._timeoutId = Registry.setTimeout(() => {
      this.hide();
      this._timeoutId = undefined;
    }, this._toastDuration);
  }

  hide() {
    this.patch({ alpha: 0 });
    this._Message.patch({ text: { text: '' } });
  }

  private updateToastWidth() {
    const newWidth = FIXED_WIDTH + this._Message.renderWidth;
    if (newWidth === FIXED_WIDTH) return;

    const newWordWrapWidth = this._maxWidth - FIXED_WIDTH;
    this.w = Math.min(this._maxWidth, newWidth);
    this._Message.patch({ text: { wordWrapWidth: newWordWrapWidth } });
    this.show();
    this.manualAnnounce(this.label);
  }

  private cleanTimers() {
    if (this._timeoutId) {
      Registry.clearTimeout(this._timeoutId);
      this._timeoutId = undefined;
    }
  }

  private manualAnnounce = debounce(
    this.handleManualAnnounce.bind(this),
    MANUAL_ANNOUNCE_DELAY,
  );

  private handleManualAnnounce(message: string) {
    this.fireAncestors('$announce', message, { append: true });
  }
}
